import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Mic, MicOff, Send } from "lucide-react";
import { Button } from "../ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUICK_COMMANDS = [
  "Create a LinkedIn campaign",
  "Design TikTok content",
  "Help me with marketing"
];

/**
 * VoiceChatModal Component - WITH CLEAR RESPONSE BUTTON
 */
export default function VoiceChatModal({ isOpen, onClose, selectedLanguage }) {
  const [message, setMessage] = useState("");
  const [response, setResponse] = useState("");
  const [loading, setLoading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const mimeTypeRef = useRef('audio/webm');

  if (!isOpen) return null;

  // Clear everything when closing
  const handleClose = () => {
    setMessage("");
    setResponse("");
    setLoading(false);
    onClose();
  };

  // Clear just the response
  const clearResponse = () => {
    setResponse("");
  };

  // Text command with streaming
  const sendCommand = async (text = message) => {
    if (!text.trim()) return;
    setLoading(true);
    setResponse("");
    
    try {
      const fetchResponse = await fetch(`${API}/text/command/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: text, language: selectedLanguage })
      });
      
      if (!fetchResponse.ok) throw new Error(`HTTP ${fetchResponse.status}`);
      
      const reader = fetchResponse.body.getReader();
      const decoder = new TextDecoder();
      let accumulatedResponse = "";
      
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.type === 'chunk') {
                accumulatedResponse += data.content;
                setResponse(accumulatedResponse);
              } else if (data.type === 'done') {
                setResponse(accumulatedResponse);
              }
            } catch (e) { /* Skip malformed JSON */ }
          }
        }
      }
      
      setMessage("");
    } catch (error) {
      console.error("Command error:", error);
      toast.error("Failed to process. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Voice command processing
  const processVoiceCommand = async (audioBlob) => {
    setLoading(true);
    setResponse("");
    
    try {
      const base64Audio = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(audioBlob);
      });
      
      const fetchResponse = await fetch(`${API}/voice/command`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          audio_base64: base64Audio,
          language: selectedLanguage
        })
      });
      
      if (!fetchResponse.ok) throw new Error('Voice processing failed');
      
      const data = await fetchResponse.json();
      
      if (data.transcribed_text) {
        setMessage(data.transcribed_text);
        toast.success(`Heard: "${data.transcribed_text}"`);
      }
      
      if (data.response) {
        setResponse(data.response);
      } else if (data.transcribed_text) {
        await sendCommand(data.transcribed_text);
      } else {
        setResponse("I couldn't understand. Please try again or type.");
      }
    } catch (error) {
      console.error("Voice error:", error);
      toast.error("Voice processing failed. Please type.");
    } finally {
      setLoading(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Try to use webm with opus codec, fallback to default
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') 
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
          ? 'audio/webm'
          : 'audio/mp4';
      
      mimeTypeRef.current = mimeType;
      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType });
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      
      mediaRecorderRef.current.onstop = async () => {
        stream.getTracks().forEach(track => track.stop());
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeTypeRef.current });
        if (audioBlob.size > 0) {
          toast.info("Processing voice...");
          
          // Use FormData for more reliable file upload
          try {
            setLoading(true);
            const formData = new FormData();
            const fileExtension = mimeTypeRef.current.includes('mp4') ? 'mp4' : 'webm';
            formData.append('audio', audioBlob, `recording.${fileExtension}`);
            formData.append('language', selectedLanguage || 'en');
            
            const response = await fetch(`${API}/voice/command-file`, {
              method: 'POST',
              body: formData
            });
            
            if (!response.ok) {
              const error = await response.json();
              throw new Error(error.detail || 'Voice processing failed');
            }
            
            const data = await response.json();
            
            if (data.transcribed_text) {
              setMessage(data.transcribed_text);
              toast.success(`Heard: "${data.transcribed_text}"`);
            }
            
            if (data.response) {
              setResponse(data.response);
            }
          } catch (error) {
            console.error("Voice error:", error);
            toast.error(error.message || "Voice processing failed. Please type your command.");
          } finally {
            setLoading(false);
          }
        }
      };
      
      mediaRecorderRef.current.start();
      setIsRecording(true);
      toast.success("🎤 Recording... Tap again to stop");
    } catch (error) {
      toast.error("Could not access microphone");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={handleClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="glass-card rounded-2xl p-6 max-w-md w-full border border-white/10"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center">
                <Mic className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold">YASNO Marketing Agent</h3>
                <p className="text-xs text-muted-foreground">Speak or type your marketing request</p>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={handleClose} className="h-8 w-8 p-0">
              <X className="w-4 h-4" />
            </Button>
          </div>

          {/* Voice Orb */}
          <div className="flex flex-col items-center mb-6">
            <button
              onClick={isRecording ? stopRecording : startRecording}
              disabled={loading}
              className={`
                w-20 h-20 rounded-full flex items-center justify-center transition-all mb-2
                ${loading ? 'opacity-50 cursor-not-allowed' : ''}
                ${isRecording 
                  ? 'bg-green-500/30 border-2 border-green-500' 
                  : 'bg-primary/20 border-2 border-primary/50 hover:bg-primary/30'
                }
              `}
              style={{
                boxShadow: isRecording ? '0 0 25px rgba(34, 197, 94, 0.5)' : 'none'
              }}
              data-testid="voice-chat-orb"
            >
              {isRecording ? (
                <MicOff className="w-8 h-8 text-green-400" />
              ) : (
                <Mic className="w-8 h-8 text-primary" />
              )}
            </button>
            <p className="text-sm text-muted-foreground">{isRecording ? 'Tap to stop' : 'Tap to speak'}</p>
          </div>

          {/* Divider */}
          <div className="flex items-center gap-4 mb-4">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-xs text-muted-foreground">or type your request</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          {/* Text Input */}
          <div className="flex gap-2 mb-4">
            <input
              type="text"
              placeholder="Create a TikTok campaign about..."
              className="flex-1 px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-sm focus:border-primary focus:outline-none"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && sendCommand()}
              disabled={loading}
            />
            <Button onClick={() => sendCommand()} disabled={!message.trim() || loading} className="bg-primary hover:bg-primary/90">
              <Send className="w-4 h-4" />
            </Button>
          </div>

          {/* Quick Commands */}
          <div className="mb-4">
            <p className="text-xs text-muted-foreground mb-2">Try saying:</p>
            <div className="flex flex-wrap gap-2">
              {QUICK_COMMANDS.map((cmd) => (
                <button
                  key={cmd}
                  onClick={() => { setMessage(cmd); sendCommand(cmd); }}
                  disabled={loading}
                  className="text-xs px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 transition-colors disabled:opacity-50"
                >
                  {cmd}
                </button>
              ))}
            </div>
          </div>

          {/* Response - WITH X BUTTON TO CLEAR */}
          {(response || loading) && (
            <div className="p-4 rounded-lg bg-primary/10 border border-primary/20 max-h-48 overflow-y-auto relative">
              {/* X button to clear response */}
              {response && !loading && (
                <button
                  onClick={clearResponse}
                  className="absolute top-2 right-2 p-1 rounded-full hover:bg-white/10 transition-colors"
                  title="Clear response"
                >
                  <X className="w-3 h-3 text-muted-foreground" />
                </button>
              )}
              <p className="text-xs font-medium text-primary mb-2">YASNO:</p>
              {response ? (
                <p className="text-sm whitespace-pre-line pr-6">{response}</p>
              ) : (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span className="flex gap-1">
                    <span className="w-2 h-2 bg-primary rounded-full animate-bounce" />
                    <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </span>
                  Thinking...
                </div>
              )}
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
